/**
 * Recuperação do "chunk que sumiu no deploy", num lugar só.
 *
 * Dois caminhos percebem a mesma falha: o `lazyRetry`, quando a rota nova não
 * carrega, e o ouvinte global do `main.tsx`, quando o erro escapa por fora do
 * React. Antes cada um tinha a sua chave de controle no sessionStorage, então
 * os dois se achavam "o primeiro" e a página podia recarregar duas vezes
 * seguidas. Chave única aqui, e quem recarrega é esta função.
 */

import { VERSAO_DO_BUNDLE, ehVersaoValida } from "@/lib/novaVersao";

/** Janela em que um segundo recarregamento é considerado laço, não recuperação. */
const JANELA_MS = 10_000;
const CHAVE = "__chunk_reload_ts__";

export function isChunkError(err: unknown): boolean {
  const s = String((err as any)?.message ?? err ?? "");
  return (
    s.includes("Failed to fetch dynamically imported module") ||
    s.includes("Importing a module script failed") ||
    s.includes("error loading dynamically imported module") ||
    /ChunkLoadError/i.test(s)
  );
}

function marcado(): number {
  try {
    return Number(sessionStorage.getItem(CHAVE) || 0);
  } catch {
    // Navegador com armazenamento bloqueado: sem memória do recarregamento
    // anterior, o melhor é não recarregar e deixar o erro aparecer — laço de
    // recarregamento é pior que tela de erro.
    return Date.now();
  }
}

/** Intervalo entre as perguntas ao servidor enquanto ele não volta. */
const INTERVALO_MS = 2_000;
/** Perguntas antes de recarregar mesmo assim: ~2 minutos. */
const TENTATIVAS = 60;
/** Pergunta que não responde nisso conta como servidor fora. */
const LIMITE_PERGUNTA_MS = 5_000;

/**
 * O servidor do front está atendendo? No deploy, o container antigo sai antes
 * de o novo subir, e nesse intervalo quem responde é o proxy, com 502. Qualquer
 * resposta abaixo de 500 é do nosso servidor — inclusive 404, que é o que o
 * `npm run dev` dá para o `version.txt`, que só existe na imagem.
 */
async function servidorResponde(): Promise<boolean> {
  const controle = new AbortController();
  const limite = setTimeout(() => controle.abort(), LIMITE_PERGUNTA_MS);
  try {
    const resposta = await fetch(`/version.txt?t=${Date.now()}`, {
      cache: "no-store",
      signal: controle.signal,
    });
    return resposta.status < 500;
  } catch {
    return false;
  } finally {
    clearTimeout(limite);
  }
}

/**
 * Espera o servidor voltar. Devolve `false` se desistiu, e aí quem chamou
 * recarrega mesmo assim — ficar parado para sempre não é melhor que a página
 * de erro.
 */
export async function esperarServidor(
  responde: () => Promise<boolean> = servidorResponde,
  dormir: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<boolean> {
  for (let i = 0; i < TENTATIVAS; i++) {
    if (await responde()) return true;
    await dormir(INTERVALO_MS);
  }
  return false;
}

let aguardando = false;

/**
 * Recarrega a página por causa de um chunk que não existe mais.
 *
 * Só recarrega depois que o servidor responde. A falha de chunk costuma
 * acontecer justamente no meio do deploy, e recarregar na hora levava a aba
 * para a página de erro do proxy, de onde ela não sai sozinha — no ChatContar
 * desktop, até alguém clicar em Recarregar. Enquanto espera, a tela fica como
 * está (o `lazyRetry` segura o "carregando").
 *
 * Devolve `false` quando já houve um recarregamento nos últimos segundos: aí o
 * índice novo já foi buscado e mesmo assim faltou o arquivo, o que não é mais
 * deploy em andamento — é falha que merece virar ocorrência.
 */
export function recarregarPorChunk(
  esperar: () => Promise<unknown> = esperarServidor,
): boolean {
  // Outro chunk falhou enquanto a espera corre: é o mesmo deploy, e a recarga
  // já está marcada. Recusar aqui faria o erro subir como ocorrência.
  if (aguardando) return true;
  if (Date.now() - marcado() < JANELA_MS) return false;
  try {
    sessionStorage.setItem(CHAVE, String(Date.now()));
  } catch {
    return false;
  }
  aguardando = true;
  const recarregar = () => {
    // Carimba de novo: a espera pode ter passado da janela, e é a partir da
    // recarga que uma segunda falha quer dizer laço.
    try {
      sessionStorage.setItem(CHAVE, String(Date.now()));
    } catch {
      // A marca da decisão continua lá; segue.
    }
    aguardando = false;
    window.location.reload();
  };
  // Espera que falhe por qualquer motivo não pode deixar a aba parada.
  void esperar().then(recarregar, recarregar);
  return true;
}

/**
 * O que o TI precisa saber de um chunk que chegou a virar ocorrência.
 *
 * A mesma mensagem — `Failed to fetch dynamically imported module` — cobre
 * casos que se tratam de jeitos opostos: arquivo que o deploy levou, arquivo
 * que falta na versão no ar (defeito de build) e arquivo que está lá e o
 * navegador não conseguiu baixar (rede, antivírus, extensão). Em 08/10/2026
 * separar um do outro exigiu baixar o índice e o arquivo da produção à mão:
 * a ocorrência dizia "chunk ausente" de um arquivo que respondia 200.
 */
export type DiagnosticoChunk = {
  /** Status do arquivo pedido de novo agora, sem cache. */
  arquivoNoServidor: number | "sem resposta" | "não verificado";
  /** O `/version.txt` respondeu sem erro de servidor. */
  servidorNoAr: boolean;
  /** A aba roda a versão que o servidor publica? */
  versaoDaAba: "atual" | "antiga" | "não verificada";
  navegadorOnline: boolean;
  /** Se o `recarregarPorChunk` chegou a rodar nesta aba, e quando. */
  recargaAutomatica: string;
  /** A conclusão, em uma frase, para quem lê o aviso. */
  leitura: string;
};

/** Pergunta que não responde nisso conta como sem resposta. */
const LIMITE_DIAGNOSTICO_MS = 3_000;

/** URL do arquivo, quando o navegador a põe na mensagem (Chrome e Firefox põem; Safari não). */
export function urlDoChunk(err: unknown): string | null {
  const s = String((err as any)?.message ?? err ?? "");
  return s.match(/https?:\/\/[^\s"')]+/)?.[0] ?? null;
}

async function perguntar(url: string, method: "GET" | "HEAD"): Promise<Response | null> {
  const controle = new AbortController();
  const limite = setTimeout(() => controle.abort(), LIMITE_DIAGNOSTICO_MS);
  try {
    return await fetch(url, { method, cache: "no-store", signal: controle.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(limite);
  }
}

function recargaAutomatica(agora: number): string {
  try {
    const ts = Number(sessionStorage.getItem(CHAVE) || 0);
    if (!ts) return "não houve";
    const s = Math.max(0, Math.round((agora - ts) / 1000));
    return s < 120 ? `há ${s}s` : `há ${Math.round(s / 60)} min`;
  } catch {
    return "não pôde rodar (armazenamento bloqueado)";
  }
}

export function lerDiagnostico(d: Omit<DiagnosticoChunk, "leitura" | "recargaAutomatica">): string {
  const { arquivoNoServidor: arquivo, versaoDaAba } = d;
  if (!d.navegadorOnline) return "O navegador estava sem rede.";
  if (arquivo === 404 && versaoDaAba === "antiga") {
    return "Aba em versão antiga pedindo arquivo que o deploy já removeu.";
  }
  if (arquivo === 404 && versaoDaAba === "atual") {
    return "Falta no servidor um arquivo da versão que está no ar: build ou deploy incompleto.";
  }
  if (arquivo === 404) return "O arquivo não existe no servidor.";
  if (typeof arquivo === "number" && arquivo < 400) {
    return "O arquivo está no servidor: o navegador não conseguiu baixá-lo (rede instável, antivírus ou extensão).";
  }
  if (typeof arquivo === "number") return `O servidor respondeu ${arquivo} ao pedir o arquivo.`;
  if (!d.servidorNoAr) return "O servidor não respondeu a esta aba: rede do usuário ou servidor fora do ar.";
  if (arquivo === "sem resposta") {
    return "O servidor responde, mas este arquivo não chega: algo no caminho o bloqueia (extensão, antivírus ou proxy).";
  }
  return "Não foi possível verificar o arquivo.";
}

/**
 * Pede de novo, sem cache, o arquivo que falhou e o `/version.txt`. Não
 * rejeita: diagnóstico que falha devolve o que conseguiu apurar.
 *
 * Só pergunta pelo arquivo se ele for da mesma origem da página — a URL vem de
 * uma mensagem de erro, e não há motivo para a tela sair batendo em endereço
 * de terceiro.
 */
export async function diagnosticarChunk(
  err: unknown,
  versaoDaAba: string = VERSAO_DO_BUNDLE,
): Promise<DiagnosticoChunk> {
  const url = urlDoChunk(err);
  let mesmaOrigem = false;
  try {
    mesmaOrigem = !!url && new URL(url).origin === window.location.origin;
  } catch {
    // URL malformada: fica como não verificado.
  }

  const [arquivo, versao] = await Promise.all([
    mesmaOrigem ? perguntar(url!, "HEAD") : Promise.resolve(undefined),
    perguntar(`/version.txt?t=${Date.now()}`, "GET"),
  ]);

  let servida: string | null = null;
  if (versao?.ok) {
    try {
      servida = (await versao.text()).trim();
    } catch {
      // Corpo que não chegou: versão fica não verificada.
    }
  }

  const base = {
    arquivoNoServidor: arquivo === undefined ? "não verificado" as const : arquivo?.status ?? "sem resposta" as const,
    servidorNoAr: !!versao && versao.status < 500,
    versaoDaAba:
      ehVersaoValida(versaoDaAba) && ehVersaoValida(servida)
        ? versaoDaAba.trim().toLowerCase() === servida.toLowerCase() ? "atual" as const : "antiga" as const
        : "não verificada" as const,
    navegadorOnline: navigator.onLine,
  };
  // A leitura primeiro: o aviso do Eugênio lista as chaves nesta ordem.
  return { leitura: lerDiagnostico(base), ...base, recargaAutomatica: recargaAutomatica(Date.now()) };
}
